import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { resetHeightChannelOverview } from '../../functions/ResetHeightChannelOverview';
import translations from "../../../text/get-started-translations.json";
import language from '../../../utils/LanguageSettings';
import { SOURCE_URL } from '../../../config';
import notFoundWbgE from "../../../images/icon/notfound_vim_wbg_e.png"

export const ChannelButton: React.FC<{ items: any[]; channelSelectedGenre: string; librarySelectedGenre: string; selectedCategory: string }> =
  React.memo(({ items, channelSelectedGenre, librarySelectedGenre, selectedCategory }) => {
    const [searchParams] = useSearchParams();
    const channelid = searchParams.get('channelid');
    const libid = searchParams.get('libid');

    const [openedItem, setOpenedItem] = useState<string | null>(() => {
      if (selectedCategory === "on-demand" && libid) {
        return libid;
      }
      if (selectedCategory === "channel" && channelid) {
        return channelid;
      }
      return null;
    });
    const [width, setWidth] = useState(window.innerWidth);
    const isInitialMount = useRef(true);
    
    const handleToggle = (itemId: string) => {
      setOpenedItem(openedItem === itemId ? null : itemId);
    };

    useEffect(() => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
      } else {
        setOpenedItem(null);
      }
    }, [channelSelectedGenre, librarySelectedGenre, selectedCategory]);

    const filteredItems = useMemo(() => {
      return items.filter((item) => {
        const selectedGenre = selectedCategory === "on-demand" ? librarySelectedGenre : channelSelectedGenre;
        const matchesGenre = selectedGenre === "all" || item.attributes.ChannelOverview?.Genre?.includes(selectedGenre);
        const matchesCategory = selectedCategory === "channel" ? item.source === "channels" : item.source === "libraries";
        return matchesGenre && matchesCategory;
      });
    }, [items, channelSelectedGenre, librarySelectedGenre, selectedCategory]);

    useEffect(() => {
      const targetId = selectedCategory === "on-demand" ? libid : channelid;
      if (targetId && filteredItems.length > 0) {
        const timer = setTimeout(() => {
          const cleanTargetId = targetId.replace(/\s+/g, "-");
          let activeElement = document.querySelector(`#collapse${cleanTargetId}`);
          
          if (activeElement) {
            activeElement.scrollIntoView({ behavior: "smooth", block: "end" });
          } else {
            // Fallback to the channel button if the collapse element is not found
            activeElement = document.querySelector(`.channel[data-id="${targetId}"]`);
            if (activeElement) {
              activeElement.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }
        }, 400);
        return () => clearTimeout(timer);
      }
    }, [filteredItems, channelid, libid, selectedCategory]);


    const rows = useMemo(
      () => groupItemsIntoRows(filteredItems),
      [filteredItems, width]
    );

    useEffect(() => {
      function changeWidth() {
        setWidth(window.innerWidth);
      }
      window.addEventListener('resize', changeWidth);
      changeWidth();

      // ADD THIS RETURN BLOCK TO CLEAN UP THE LISTENER
      return () => {
        window.removeEventListener('resize', changeWidth);
      };
    }, []);

    useEffect(() => {
      resetHeightChannelOverview();
    });
    return (
      <div className="accordion" id="accordionOverview">
        {rows.map((row, rowIndex) => (
          <React.Fragment key={rowIndex}>
            <div className="container">
              <div className="row">
                {row.map(
                  (item) =>
                    item.attributes && (
                      <div key={item.attributes.number} className="item">
                        <ChannelButtonItem
                          item={item}
                          isActive={openedItem === item.attributes.number}
                          onToggle={handleToggle}
                        />
                      </div>
                    )
                )}
              </div>
            </div>
            <div className="container-fluid">
              <div className="row">
                {row.map(
                  (item) =>
                    item.attributes && (
                      <div key={item.attributes.number} className="col-12">
                        <ChannelAccordionItem
                          item={item}
                          isActive={openedItem === item.attributes.number}
                        />
                      </div>
                    )
                )}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  });

const ChannelButtonItem: React.FC<{
  item: any;
  isActive: boolean;
  onToggle: (itemId: string) => void;
}> = ({ item, isActive, onToggle }) => {
  const pageTranslations = translations[language];
  const isLibrary = item.source === "libraries";
  const iconUrl = item.attributes.icon.data
    ? SOURCE_URL + item.attributes.icon.data.attributes.url
    : `${notFoundWbgE}`
  return (
    <button
      className={`channel ${isActive ? "active" : ""}`}
      data-id={item.attributes.number}
      data-category={`category-${item.attributes.number}`}
      data-name={item.attributes.name}
      data-desc={item.attributes.description}
      data-link={`https://nowplayer.now.com/tvguide/channeldetail/${item.attributes.number}/1?isfromchannel=false`}
      data-label="Channel Schedule"
      data-bs-toggle="collapse"
      type="button"
      data-bs-target={`#collapse${item.attributes.number}`}
      aria-expanded={isActive ? "true" : "false"}
      aria-controls={`collapse${item.attributes.number}`}
      onClick={() => onToggle(item.attributes.number)}
    >
      <div className={`channel-content${isActive ? " active" : ""}`}>
        <span className="text-16">
          {isLibrary ? item.attributes.name : language === "en" ? `${pageTranslations.CH} ${item.attributes.number}` : `${item.attributes.number} ${pageTranslations.CH}`}
        </span>
        <div className="channel-logo">
          <img
            data-url={iconUrl}
            className="img-fluid lazy apply-default"
            alt=""
            data-default={notFoundWbgE}
            src={iconUrl}
            onError={(e) => {
              e.currentTarget.src = `${notFoundWbgE}`
            }}
          />
        </div>
      </div>
    </button>
  );
};

const ChannelAccordionItem: React.FC<{ item: any; isActive: boolean }> = ({
  item,
  isActive,
}) => {
  const itemId = item?.attributes?.number?.replace(/\s+/g, "-") || ""; 
  const channelOverview = item.attributes.ChannelOverview;
  const linkUrl = channelOverview?.URL;
  const linkLabel = linkUrl ? channelOverview?.Label || (item.source === "channels" ? translations[language].ChannelSchedule : translations[language].WatchonNowTVAppWebsite) : '';

  const channelOverviewButton = channelOverview?.ChannelOverviewButton;

  let extraButton = null;

  if (channelOverviewButton === "See Plan") {
    const isChannel = item.source === "channels";
    const btnParam = isChannel ? `channelid=${item.attributes.number}` : `libid=${item.attributes.number}`;
    extraButton = (
      <Link
        to={`/get-started?${btnParam}`}
        className="btn primary"
      >
        {translations[language].SeePlanButton}
      </Link>
    );
  } else if (channelOverviewButton === "Contact us") {
    extraButton = (
      <Link
        to="/site/enquiry3"
        className="btn primary"
      >
        {translations[language].ContactUsButton}
      </Link>
    );
  }

  return (
    <div
      className={`accordion-item channel_description ${isActive ? "active" : ""
        }`}
    >
      <div
        id={`collapse${itemId}`}
        className={`accordion-collapse collapse ${isActive ? "show" : ""
          }`}
        aria-labelledby={`heading${itemId}`}
        data-bs-parent="#accordionOverview"
      >
        <div className="accordion-body">
          <div className="inner-wrap container px-4">
            <div className="wrapper">
              <div className="title">
                <h3 className="text-20 title">{item.attributes.name}</h3>
                {(linkUrl || extraButton) && (
                  <div className="channel-actions">
                    {linkUrl && (
                      <Link
                        to={linkUrl}
                        className="text-16 nowplayer_link barlow-font color-4"
                        target="_blank"
                      >
                        {linkLabel}
                      </Link>
                    )}
                    {extraButton}
                  </div>
                )}
              </div>
              <div className="description">
                <p className="text-16 channel_desc">
                  {item.attributes.description || "\u00A0"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const groupItemsIntoRows = (items: any[]) => {
  const rows = [];
  const itemsPerRowDynamic = getItemsPerRow();
  for (let i = 0; i < items.length; i += itemsPerRowDynamic) {
    rows.push(items.slice(i, i + itemsPerRowDynamic));
  }
  return rows;
};

const getItemsPerRow = () => {
  if (window.innerWidth >= 1200) {
    return 6;
  } else if (window.innerWidth >= 992) {
    return 5;
  } else if (window.innerWidth >= 768) {
    return 4;
  } else if (window.innerWidth >= 576) {
    return 3;
  } else {
    return 2;
  }
};
